import {
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Text,
  Box,
  Flex,
  useColorModeValue,
  Button,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { Icon } from '~/packages/common-frontend/components/icons';

// Constants
const CREDIT_FACILITY_COLUMN_DEF = [
  { accessorKey: 'name', header: 'Facility' },
];

// Exports
type SettingsFacilitiesTableProps = {
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'name'>[];
};
export const SettingsFacilitiesTable = ({
  creditFacilities,
}: SettingsFacilitiesTableProps) => {
  const borderColor = useColorModeValue('gray.100', 'gray.900');
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: creditFacilities,
    columns: CREDIT_FACILITY_COLUMN_DEF,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  return (
    <Flex width="100%" overflowX="auto">
      <Table
        borderWidth="1px"
        borderColor={borderColor}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                return (
                  <Th key={header.id} whiteSpace="nowrap">
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>

                      <Box mr="8px">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => {
            const creditFacilityName = z.string().parse(row.getValue('name'));
            return (
              <Tr key={row.id}>
                <Td padding="0.25rem">
                  <Button
                    as={Link}
                    variant="v2-ghost"
                    colorScheme="gray"
                    fontWeight={400}
                    width="100%"
                    justifyContent="space-between"
                    to={`./${row.original.id}/borrowing-base/configuration`}
                    gap="1rem"
                  >
                    {creditFacilityName}
                    <Flex as="span" gap="0.5rem">
                      <Text as="span" fontWeight={400} fontSize="0.875rem">
                        View Settings
                      </Text>
                      <Icon icon="chevron-right" />
                    </Flex>
                  </Button>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Flex>
  );
};
